"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import { SectionHeader, CTAButton, ContentCard, type ImageComponent } from "fansunited-sports-ui";
import { FillImage } from "@/components/ui/FillImage";
import { MediaVariantsProvider } from "@/components/ui/MediaVariantsProvider";
import { getPostImageUrl } from "@/components/ui/PostImage";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import type { Post } from "@/payload-types";
export interface LatestPostsGridProps {
  /** Posts to display. Sliced to 12 max internally. */
  posts: Post[];
}

/**
 * LatestPostsGrid — Fallback grid of recent posts shown when no homepage sections list is
 * configured for the tenant. Renders a 3-column grid of standard ContentCard cards, each
 * with a hero image, title, and summary.
 *
 * Typically used by Basic-type homepages as their primary content display.
 * Shows up to 12 posts; renders nothing when the array is empty.
 */
export function LatestPostsGrid(t0) {
  const $ = _c(36);
  const {
    posts
  } = t0;
  const labels = useLabels();
  const nav = useNavigation();
  let T0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== labels || $[1] !== nav || $[2] !== posts) {
    t5 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const items = posts.slice(0, 12);
      if (items.length === 0) {
        t5 = null;
        break bb0;
      }
      let t6;
      if ($[9] !== labels.homePage.latestNews) {
        t6 = labels.homePage.latestNews.toUpperCase();
        $[9] = labels.homePage.latestNews;
        $[10] = t6;
      } else {
        t6 = $[10];
      }
      let t7;
      if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = {
          typography: {
            name: "text-sm uppercase font-bold text-foreground tracking-wider"
          }
        };
        $[11] = t7;
      } else {
        t7 = $[11];
      }
      let t8;
      if ($[12] !== t6) {
        t8 = {
          name: t6,
          styles: t7
        };
        $[12] = t6;
        $[13] = t8;
      } else {
        t8 = $[13];
      }
      const t9 = labels.homePage.viewAll;
      let t10;
      if ($[14] !== nav) {
        t10 = nav.articles();
        $[14] = nav;
        $[15] = t10;
      } else {
        t10 = $[15];
      }
      let t11;
      if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
        t11 = {
          container: "text-sm font-medium text-accent hover:text-accent/80 border-none p-0"
        };
        $[16] = t11;
      } else {
        t11 = $[16];
      }
      let t12;
      if ($[17] !== labels.homePage.viewAll || $[18] !== t10) {
        t12 = <CTAButton text={t9} href={t10} variant="custom" styles={t11} />;
        $[17] = labels.homePage.viewAll;
        $[18] = t10;
        $[19] = t12;
      } else {
        t12 = $[19];
      }
      let t13;
      if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
        t13 = {
          container: "mb-4"
        };
        $[20] = t13;
      } else {
        t13 = $[20];
      }
      if ($[21] !== t12 || $[22] !== t8) {
        t4 = <SectionHeader entity={t8} primaryCta={t12} styles={t13} />;
        $[21] = t12;
        $[22] = t8;
        $[23] = t4;
      } else {
        t4 = $[23];
      }
      T0 = MediaVariantsProvider;
      t3 = items.map(_temp);
      t1 = "grid grid-cols-1 md:grid-cols-3 gap-4";
      let t14;
      if ($[24] !== nav) {
        t14 = post_0 => <ContentCard key={post_0.id} content={{
          id: String(post_0.id),
          type: post_0.type === "video" ? "video" : "article",
          link: nav.post(post_0),
          title: post_0.title,
          description: post_0.summary || undefined,
          image: {
            url: getPostImageUrl(post_0, "medium"),
            alt: post_0.title
          }
        }} variant="standard" ImageComponent={FillImage as ImageComponent} styles={{
          shadow: "sm",
          borderRadius: "lg",
          typography: {
            title: "text-base font-bold mb-1 line-clamp-2",
            description: "text-sm text-muted-foreground line-clamp-2"
          }
        }} />;
        $[24] = nav;
        $[25] = t14;
      } else {
        t14 = $[25];
      }
      t2 = items.map(t14);
    }
    $[0] = labels;
    $[1] = nav;
    $[2] = posts;
    $[3] = T0;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    T0 = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
    t5 = $[8];
  }
  if (t5 !== Symbol.for("react.early_return_sentinel")) {
    return t5;
  }
  let t6;
  if ($[26] !== t1 || $[27] !== t2) {
    t6 = <div className={t1}>{t2}</div>;
    $[26] = t1;
    $[27] = t2;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  let t7;
  if ($[29] !== T0 || $[30] !== t3 || $[31] !== t6) {
    t7 = <T0 media={t3}>{t6}</T0>;
    $[29] = T0;
    $[30] = t3;
    $[31] = t6;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  let t8;
  if ($[33] !== t4 || $[34] !== t7) {
    t8 = <section>{t4}{t7}</section>;
    $[33] = t4;
    $[34] = t7;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  return t8;
}
function _temp(post) {
  return post.heroImage;
}